import PropTypes from 'prop-types';
import { alpha, styled } from '@mui/material/styles';
import {
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import MovieCreationOutlinedIcon from '@mui/icons-material/MovieCreationOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';

const UploadCard = styled(Card)(({ theme }) => ({
  borderRadius: 18,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const UploadZone = styled(Box)(({ theme }) => ({
  minHeight: 560,
  borderRadius: 18,
  border: `1px dashed ${alpha(theme.palette.text.secondary, 0.22)}`,
  backgroundColor: alpha(theme.palette.background.neutral, 0.32),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
}));

const SupportCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
}));

const FormatPill = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 14,
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.palette.background.paper,
  minWidth: 150,
}));

function SideInfoCard({ icon, title, description, action }) {
  return (
    <SupportCard>
      <Stack spacing={1.6}>
        <Avatar
          variant="rounded"
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2.5,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
          }}
        >
          {icon}
        </Avatar>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
          {description}
        </Typography>
        {action}
      </Stack>
    </SupportCard>
  );
}

export default function UploadDataView({ theme }) {
  return {
    main: (
      <UploadCard>
        <Stack spacing={3}>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <CloudUploadOutlinedIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h4" sx={{ fontSize: { xs: 24, md: 30 }, fontWeight: 700 }}>
              Upload
            </Typography>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.8, fontWeight: 700 }}>
                Batch Name:
              </Typography>
              <TextField
                fullWidth
                size="small"
                defaultValue="Uploaded on 04/21/26 at 6:16 pm"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.8 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Tags:
                </Typography>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
              </Stack>
              <TextField
                fullWidth
                size="small"
                placeholder="Search or add tags for images..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SellOutlinedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
              />
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Checkbox size="small" />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Create batch instantly
            </Typography>
          </Stack>

          <UploadZone>
            <Stack spacing={3} alignItems="center" sx={{ width: '100%', maxWidth: 520, textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                }}
              >
                <CloudUploadOutlinedIcon sx={{ fontSize: 28 }} />
              </Avatar>

              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Drag and drop file(s) to upload, or:
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                <Button variant="outlined" startIcon={<InsertDriveFileOutlinedIcon />} sx={{ borderRadius: 2, textTransform: 'none' }}>
                  Select File(s)
                </Button>
                <Button variant="outlined" startIcon={<FolderOpenOutlinedIcon />} sx={{ borderRadius: 2, textTransform: 'none' }}>
                  Select Folder
                </Button>
              </Stack>

              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.4 }}>
                SUPPORTED FORMATS
              </Typography>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ width: '100%', justifyContent: 'center', alignItems: 'stretch' }}>
                <FormatPill>
                  <Stack spacing={0.6}>
                    <Stack direction="row" spacing={0.8} alignItems="center">
                      <ImageOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Images
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      .jpg, .png, .bmp, .webp, .avif
                    </Typography>
                  </Stack>
                </FormatPill>

                <FormatPill>
                  <Stack spacing={0.6}>
                    <Stack direction="row" spacing={0.8} alignItems="center">
                      <SellOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Annotations
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      26 formats supported
                    </Typography>
                  </Stack>
                </FormatPill>

                <FormatPill>
                  <Stack spacing={0.6}>
                    <Stack direction="row" spacing={0.8} alignItems="center">
                      <MovieCreationOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Videos
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      .mov, .mp4
                    </Typography>
                  </Stack>
                </FormatPill>

                <FormatPill>
                  <Stack spacing={0.6}>
                    <Stack direction="row" spacing={0.8} alignItems="center">
                      <DescriptionOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        PDFs
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      .pdf
                    </Typography>
                  </Stack>
                </FormatPill>
              </Stack>

              <Typography variant="caption" color="text.disabled">
                Max size of 20MB and 16,400 x 10,900 pixels.
              </Typography>
            </Stack>
          </UploadZone>
        </Stack>
      </UploadCard>
    ),
    side: (
      <Stack spacing={2}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Need images to get started?
        </Typography>

        <SideInfoCard
          icon={<PhoneIphoneRoundedIcon />}
          title="Upload data from your phone"
          description="Scan the QR code to upload images and videos from your phone directly to your project."
          action={(
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 68,
                  height: 68,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  background:
                    'linear-gradient(45deg, #000 12%, #fff 12%, #fff 24%, #000 24%, #000 36%, #fff 36%, #fff 48%, #000 48%, #000 60%, #fff 60%, #fff 72%, #000 72%)',
                  backgroundSize: '18px 18px',
                }}
              />
              <Typography variant="body2" color="text.secondary">
                Use the code from mobile to send media straight into this workspace.
              </Typography>
            </Stack>
          )}
        />

        <SideInfoCard
          icon={<TravelExploreRoundedIcon />}
          title="Search on Roboflow Universe"
          description="Search across public datasets and images to quickly bootstrap your vision project."
          action={(
            <Stack direction="row" spacing={1}>
              <TextField fullWidth size="small" placeholder="Search for images" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              <IconButton sx={{ width: 40, height: 40, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                <ArrowForwardRoundedIcon />
              </IconButton>
            </Stack>
          )}
        />

        <SideInfoCard
          icon={<CodeRoundedIcon />}
          title="Bulk Upload Images"
          description="Upload large batches using Python SDK, REST API, or CLI integrations."
          action={<Button variant="outlined" startIcon={<CodeRoundedIcon />} sx={{ borderRadius: 2, textTransform: 'none' }}>Learn More</Button>}
        />

        <SupportCard>
          <Stack direction="row" spacing={1} alignItems="center">
            <PublicRoundedIcon sx={{ color: 'primary.main' }} />
            <Typography variant="body2" color="text.secondary">
              Theme-aware preview page built in `Test.js`.
            </Typography>
          </Stack>
        </SupportCard>
      </Stack>
    ),
  };
}

UploadDataView.propTypes = {
  theme: PropTypes.object.isRequired,
};

SideInfoCard.propTypes = {
  action: PropTypes.node,
  description: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
};

SideInfoCard.defaultProps = {
  action: null,
};
